import { Col, Progress, Row, notification } from "antd";
import { get, union } from "lodash";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdOutlineDescription, MdOutlineEditNote } from "react-icons/md";
import { useParams } from "react-router-dom";
import Alert from "shared/components/Alert";
import Button from "shared/components/Button";
import Form from "shared/components/Form";
import { useMutation } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import format from "string-template";
import graphql from "utils/api/graphql";
import { GENERATE_SUBSCRIPTION_DOCUMENTS } from "utils/api/graphql/mutations/attachment";
import { SIGN_DOCUMENT } from "utils/api/graphql/mutations/esign";
import {
  DATE_TIME_FORMAT,
  DOCUMENTS_GENERATION_MODES,
  INITIAL_VALUES,
  SUBSCRIPTION_STATUSES,
} from "utils/constants";
import {
  getAcorisQuoteReceptionDate,
  hasAcorisAdherentMobilePhone,
  isAcorisEsignCoolingOffActive,
  shouldGateEsignOnComplementaryInfo,
} from "./esign";
import { STEPPER_KEYS } from "../../../..";
import SubscriptionStatus from "../elements/statuses";
import { generateDocumentsWithMode } from "../generateDocuments";

const Esignature = ({
  additionalInfoPercentage,
  proofPercentage,
  setActive,
  subscription,
  updateSubscription,
}) => {
  const [subscriptionStatus, setSubscriptionStatus] = useState(
    get(subscription, "status") || SUBSCRIPTION_STATUSES.DRAFT,
  );
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", {
    returnObjects: true,
  });
  const E_SIGNATURE = t("SUBSCRIPTION.DOCUMENT.E_SIGNATURE", {
    returnObjects: true,
  });
  useEffect(() => {
    setSubscriptionStatus(subscription.status);
  }, [subscription.status]);
  const { id } = useParams();
  const [generateSubscriptionDocuments, { loading: generateLoading }] =
    useMutation(GENERATE_SUBSCRIPTION_DOCUMENTS);
  const [signDocuments, { loading: signLoading }] = useMutation(SIGN_DOCUMENT, {
    refetchQueries: [
      {
        query: graphql.queries.SUBSCRIPTION,
        awaitRefetchQueries: true,
        variables: { where: { id } },
      },
      {
        query: graphql.queries.PROJECTS,
        awaitRefetchQueries: true,
        variables: INITIAL_VALUES,
      },
    ],
  });

  const quoteAttachments = useMemo(
    () => union(get(subscription, "attachments", [])),
    [subscription],
  );

  const hasAdherentPhone = hasAcorisAdherentMobilePhone(subscription);
  const eSignCoolingOff = isAcorisEsignCoolingOffActive(
    subscription,
    quoteAttachments,
  );
  const quoteReceptionDate = getAcorisQuoteReceptionDate(
    subscription,
    quoteAttachments,
  );

  const signDocument = () => {
    if (!hasAdherentPhone || eSignCoolingOff) {
      return;
    }
    generateDocumentsWithMode({
      updateSubscription,
      generateSubscriptionDocuments,
      subscriptionId: id,
      mode: DOCUMENTS_GENERATION_MODES.E_SIGNATURE,
      options: {
        variables: {
          data: { id },
        },
        onCompleted: () => {
          signDocuments({
            variables: {
              data: {
                id,
              },
            },
            onCompleted: () => {
              setSubscriptionStatus(SUBSCRIPTION_STATUSES.PENDING);
              notification.open({
                message: SUBSCRIPTION.E_SIGNATURE_SENT,
                duration: 5,
                type: "success",
              });
            },
          });
        },
      },
    });
  };

  const eSignDisabled =
    eSignCoolingOff || !hasAdherentPhone || generateLoading || signLoading;

  return (
    <div className="subscription-form--wrapper">
      <Form type="vertical" title={E_SIGNATURE.TITLE}>
        {shouldGateEsignOnComplementaryInfo(
          get(subscription, "status"),
          additionalInfoPercentage,
        ) ? (
          <Row gutter={[16, 16]}>
            <Col lg={24} xs={24}>
              <Alert showIcon type="error" message={E_SIGNATURE.INFO} />
            </Col>
            <Col lg={24} xs={24}>
              <div className="progress_wrapper">
                <div className="progress_wrapper--content">
                  <div className="progress_wrapper--content__title">
                    <MdOutlineDescription size={24} />
                    <span>{E_SIGNATURE.FURTHER_INFORMATION}</span>
                  </div>
                  <span className="progress_wrapper--content__percent">
                    {`${additionalInfoPercentage}% ${SUBSCRIPTION.DONE}`}
                  </span>
                </div>
                <div className="progress_wrapper--bar">
                  <Progress
                    percent={additionalInfoPercentage}
                    size={"small"}
                    showInfo={false}
                  />
                </div>
              </div>
            </Col>
            <Col lg={24} xs={24}>
              <Button
                type="primary"
                alignment="center"
                onClick={() => setActive(STEPPER_KEYS.ADDITIONAL_INFORMATION)}
              >
                {SUBSCRIPTION.FINALIZE_SUBSCRIPTION}
                <MdOutlineEditNote size={16} />
              </Button>
            </Col>
          </Row>
        ) : (
          <Col lg={24} xs={24}>
            {!hasAdherentPhone && (
              <Alert
                showIcon
                type="error"
                message={E_SIGNATURE.MISSING_PHONE}
              />
            )}
            {eSignCoolingOff && (
              <Alert
                showIcon
                type="warning"
                message={format(E_SIGNATURE.WARNING, {
                  time: moment(quoteReceptionDate)
                    .add(24, "hours")
                    .format(DATE_TIME_FORMAT),
                })}
              />
            )}
            {proofPercentage < 100 && (
              <Alert
                showIcon
                type="info"
                message={SUBSCRIPTION.E_SIGNATURE_MISSING_PROOFS_INFO}
              />
            )}
            {!hasAdherentPhone && (
              <div className="mt-16">
                <Button
                  type="primary"
                  alignment="center"
                  onClick={() => setActive(STEPPER_KEYS.ADDITIONAL_INFORMATION)}
                >
                  {SUBSCRIPTION.FINALIZE_SUBSCRIPTION}
                  <MdOutlineEditNote size={16} />
                </Button>
              </div>
            )}
            <br />
            <SubscriptionStatus
              type={subscriptionStatus}
              subscription={subscription}
              translation={{
                ...SUBSCRIPTION,
                E_SIGNATURE:
                  SUBSCRIPTION.E_SIGNATURE_SEND_REQUEST ||
                  SUBSCRIPTION.E_SIGNATURE,
              }}
              signDocument={signDocument}
              disabled={eSignDisabled}
              percentages={{
                proofPercentage,
                additionalInfoPercentage,
              }}
            />
          </Col>
        )}
      </Form>
    </div>
  );
};

export default Esignature;
